import { Check } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme
} from "@mui/material";
import i18n from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useIsMobile from "../../../hooks/ui-states/useIsMobile";
import BaseDialog from "../BaseDialog";
import DrawerBottom from "../DrawerBottom";

interface LanguageSelectorProps {
  open: boolean;
  onClose: () => void;
}

interface Language {
  key: string;
  label: string;
}

const languages: Language[] = [
  { key: "vi", label: "Tiếng Việt" },
  { key: "en", label: "Tiếng Anh" }
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ open, onClose }) => {
  const { t } = useTranslation(['settings']);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);

  const handleLanguageSelect = (languageKey: string) => {
    setSelectedLanguage(languageKey);
  };

  const handleSave = () => {
    i18n.changeLanguage(selectedLanguage);
    localStorage.setItem('language', selectedLanguage);
    onClose();
  };

  const handleClose = () => {
    // Reset to current language if user cancels
    setSelectedLanguage(i18n.language);
    onClose();
  };

  const title = t('language');

  const content = (
    <Box sx={{ p: isMobile ? 0 : 2 }}>
      {/* Language Options */}
      <List sx={{ py: 0, gap: 1, display: 'flex', flexDirection: 'column' }}>
        {languages.map((language) => (
          <ListItem key={language.key} disablePadding>
            <Card variant="outlined" sx={{ width: '100%' }}>
              <ListItemButton
                onClick={() => handleLanguageSelect(language.key)}
                sx={{
                  p: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemText
                  primary={<Typography variant="h4">{language.label}</Typography>}
                />
                {selectedLanguage === language.key && (
                  <Check sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                )}
              </ListItemButton>
            </Card>
          </ListItem>
        ))}
      </List>

      {/* Save Button */}
      <Box sx={{ mt: 4, px: 2, pb: isMobile ? 2 : 0 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600
          }}
        >
          {t('language_selector.save', 'Lưu')}
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <DrawerBottom
        open={open}
        onClose={handleClose}
        height="auto"
        title={title}
      >
        {content}
      </DrawerBottom>
    );
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      title={title}
      titleSx={{ textAlign: 'center', pb: 1 }}
      paperSx={{ borderRadius: 2, overflow: 'hidden' }}
    >
      {content}
    </BaseDialog>
  );
};

export default LanguageSelector; 